import {
    Component,
    defineElement,
    FC,
    onDidLoad,
    useBindMethod,
    useElement,
    useEvent,
    useListen,
    useProp,
} from '@atomify/hooks';
import { toCurrency } from '@bpd-library/utilities';
import noUiSlider, { noUiSlider as NoUiSlider } from 'nouislider';

export interface BPDRange extends Component {
    bindValue: number[];
    start: number[];
    resetState: () => void;
    setValues: (minValue: number, maxValue: number) => void;
}

interface SliderContainer extends HTMLDivElement {
    noUiSlider: NoUiSlider;
}

const formatValue = (value: number) => toCurrency(value).replace(',', '.');

export const RangeSlider: FC<BPDRange> = ({ element }) => {
    const [min] = useProp<number>('min', 0);
    const [max] = useProp<number>('max', 0);
    const [increment] = useProp<number>('increment', 0);
    const [hasChanged, setHasChanged] = useProp<boolean>('hasChanged', false);
    const [, setStart, watchStart] = useProp<number[]>('start', [0, 0]);
    const [, setBindValue] = useProp<number[]>('bindValue', [0, 0]);

    const sliderContainer = useElement<SliderContainer>('[js-hook-slider-container]');
    const sliderMin = useElement<HTMLInputElement | HTMLSpanElement>('[js-hook-slider-min]');
    const sliderMax = useElement<HTMLInputElement | HTMLSpanElement>('[js-hook-slider-max]');
    const sliderMinValue = useElement<HTMLInputElement>('[js-hook-slider-min-value]');
    const sliderMaxValue = useElement<HTMLInputElement>('[js-hook-slider-max-value]');
    const sliderMinMaxInput = useElement<HTMLInputElement>('[js-hook-slider-min-max-value]');

    const rangeValuesChanged = useEvent({ eventName: 'rangevalueschanged' });

    useBindMethod('resetState', resetState);
    useBindMethod('setValues', (minValue, maxValue) => {
        if (sliderMinValue.current && sliderMaxValue.current) {
            sliderMinValue.current.value = `${minValue}`;
            sliderMaxValue.current.value = `${maxValue}`;
            sliderMinValue.current.checked = true;
            sliderMaxValue.current.checked = true;
            (sliderMin.current as HTMLSpanElement)!.innerHTML = formatValue(minValue);
            (sliderMax.current as HTMLSpanElement)!.innerHTML = formatValue(maxValue);
        }

        if (sliderMinMaxInput.current) {
            sliderMinMaxInput.current.value = `${minValue},${maxValue}`;
            sliderMinMaxInput.current.checked = true;
        }

        sliderContainer.current?.noUiSlider.updateOptions({
            start: [Number(minValue), Number(maxValue)],
        });
    });
    useListen(sliderMin, 'change', changeValuesManually);
    useListen(sliderMax, 'change', changeValuesManually);

    onDidLoad(() => {
        const hasZeroValues = element.start.every((item) => item === 0);

        const settings = {
            start: hasZeroValues ? [min, max] : element.start,
            step: increment,
            connect: true,
            documentElement: sliderContainer.current,
            range: {
                min,
                max,
            },
        };

        noUiSlider.create(sliderContainer.current!, settings);

        sliderContainer.current?.noUiSlider.on('update', (values: string[]) => {
            const generatedValues = values.map((value) => parseInt(value));
            setStart([...generatedValues]);
        });

        sliderContainer.current?.noUiSlider.on('change', () => onChangeHandler('slider'));

        setMinMaxValues();
    });

    watchStart(setMinMaxValues);

    function changeValuesManually() {
        const [currMin, currMax] = element.start;

        let newMin = Number((sliderMin.current as HTMLInputElement).value);
        let newMax = Number((sliderMax.current as HTMLInputElement).value);
        newMin = Math.floor(newMin / increment) * increment;
        newMax = Math.ceil(newMax / increment) * increment;

        if (newMin > currMax) newMin = currMin;
        if (newMax < currMin) newMax = currMax;

        if (newMin < min) newMin = min;
        if (newMax > max) newMax = max;

        setStart([newMin, newMax]);
        setMinMaxValues();
        onChangeHandler('input');
        sliderContainer.current?.noUiSlider.updateOptions({
            start: [newMin, newMax],
        });
    }

    function setMinMaxValues() {
        const { minValue, maxValue } = getValues();

        if (sliderMinMaxInput.current) {
            (sliderMin.current as HTMLInputElement).value = `${minValue}`;
            (sliderMax.current as HTMLInputElement).value = `${maxValue}`;
        } else {
            (sliderMin.current as HTMLSpanElement)!.innerHTML = formatValue(minValue);
            (sliderMax.current as HTMLSpanElement)!.innerHTML = formatValue(maxValue);
        }
    }

    function onChangeHandler(rangeOrigin?: 'slider' | 'input') {
        const { minValue, maxValue } = getValues();

        if (sliderMinValue.current && sliderMaxValue.current) {
            sliderMinValue.current.value = `${minValue}`;
            sliderMaxValue.current.value = `${maxValue}`;
            sliderMinValue.current.checked = true;
            sliderMaxValue.current.checked = true;
            (sliderMin.current as HTMLSpanElement)!.innerHTML = formatValue(minValue);
            (sliderMax.current as HTMLSpanElement)!.innerHTML = formatValue(maxValue);

            sliderMinValue.current!.dispatchEvent(new Event('change'));
            sliderMaxValue.current!.dispatchEvent(new Event('change'));
        }

        if (sliderMinMaxInput.current) {
            sliderMinMaxInput.current.value = `${minValue},${maxValue}`;
            sliderMinMaxInput.current.checked = true;

            if (rangeOrigin === 'slider') {
                sliderMinMaxInput.current.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        rangeValuesChanged.emit({ minValue, maxValue, hasChanged });
    }

    function getValues() {
        const hasZeroValues = element.start.every((item) => item === 0);
        const minValue = hasZeroValues ? min : element.start[0];
        const maxValue = hasZeroValues ? max : element.start[1];

        setBindValue(element.start);
        setHasChanged(JSON.stringify(element.start) !== JSON.stringify([min, max]));

        return {
            minValue,
            maxValue,
        };
    }

    function resetState() {
        sliderContainer.current?.noUiSlider.set([min, max]);
        setStart([min, max]);
        setHasChanged(false);

        if (sliderMinMaxInput.current) {
            sliderMinMaxInput.current.value = '';
            sliderMinMaxInput.current.checked = false;
        }
    }
};

RangeSlider.props = {
    min: {
        type: Number,
        reflectToAttr: true,
    },
    max: {
        type: Number,
        reflectToAttr: true,
    },
    increment: {
        type: Number,
        reflectToAttr: true,
    },
    hasChanged: {
        type: Boolean,
        reflectToAttr: true,
    },
    start: {
        type: Array,
        reflectToAttr: true,
    },
    bindValue: {
        type: Array,
    },
};

defineElement('bpd-range-slider', RangeSlider);
